import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

// Iconos Clínicos
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';

export function useTimelineSoap(consultas) {
    const [filtroActivo, setFiltroActivo] = useState('TODOS');

    // 🚀 LÓGICA DE NEGOCIO: Motor Heurístico Aislado
    const analizarGravedad = (consulta) => {
        const textoCompuesto = `${consulta.motivo || ''} ${consulta.sintomas || ''} ${consulta.diagnostico || ''} ${consulta.tratamiento || ''}`.toLowerCase();

        const kwAlta = ['urgencia', 'agudo', 'grave', 'fractura', 'infarto', 'hemorragia', 'urgente', 'crítico', 'severo', 'traumatismo', 'rotura', 'cirugía', 'quirúrgico', 'shock', 'tumor', 'isquemia', 'trombosis'];
        const kwMedia = ['dolor', 'infección', 'crónico', 'fiebre', 'inflamación', 'moderado', 'molestia', 'virus', 'bacteria', 'mareo', 'herida', 'sutura', 'gastroenteritis', 'bronquitis', 'esguince', 'ansiedad', 'hipertensión', 'asma'];

        if (kwAlta.some(kw => textoCompuesto.includes(kw))) {
            return {
                id: 'ALTA', nivel: 'Alta Severidad',
                icon: <WarningRoundedIcon fontSize="small" />,
                cardBorder: 'border-rose-300', cardBg: 'bg-rose-50/30',
                badgeBg: 'bg-rose-100', badgeText: 'text-rose-700',
                dotBg: 'bg-rose-500', dotBorder: 'border-rose-100', shadow: 'shadow-rose-500/20'
            };
        }

        if (kwMedia.some(kw => textoCompuesto.includes(kw))) {
            return {
                id: 'MEDIA', nivel: 'Atención Moderada',
                icon: <ReportProblemRoundedIcon fontSize="small" />,
                cardBorder: 'border-amber-300', cardBg: 'bg-amber-50/30',
                badgeBg: 'bg-amber-100', badgeText: 'text-amber-700',
                dotBg: 'bg-amber-500', dotBorder: 'border-amber-100', shadow: 'shadow-amber-500/20'
            };
        }

        return {
            id: 'BAJA', nivel: 'Leve / Rutina',
            icon: <CheckCircleOutlineRoundedIcon fontSize="small" />,
            cardBorder: 'border-emerald-200', cardBg: 'bg-emerald-50/30',
            badgeBg: 'bg-emerald-100', badgeText: 'text-emerald-700',
            dotBg: 'bg-emerald-500', dotBorder: 'border-emerald-100', shadow: 'shadow-emerald-500/20'
        };
    };

    // 📊 CÁLCULO DE MÉTRICAS (Aislado del renderizado)
    const { consultasProcesadas, metricas } = useMemo(() => {
        if (!consultas) return { consultasProcesadas: [], metricas: { ALTA: 0, MEDIA: 0, BAJA: 0, TODOS: 0 } };

        const procesadas = [];
        const stats = { ALTA: 0, MEDIA: 0, BAJA: 0, TODOS: consultas.length };

        consultas.forEach(c => {
            const gravedad = analizarGravedad(c);
            stats[gravedad.id]++;
            procesadas.push({ ...c, _gravedad: gravedad });
        });

        return { consultasProcesadas: procesadas, metricas: stats };
    }, [consultas]);

    // 🔍 LÓGICA DE FILTRADO
    const consultasFiltradas = useMemo(() => {
        if (filtroActivo === 'TODOS') return consultasProcesadas;
        return consultasProcesadas.filter(c => c._gravedad.id === filtroActivo);
    }, [consultasProcesadas, filtroActivo]);

    // Utilidad de formateo
    const safeFormatDate = (dateString, formatStr) => {
        if (!dateString) return '--';
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return '--';
        return format(d, formatStr, { locale: es });
    };

    return {
        filtroActivo, setFiltroActivo,
        consultasFiltradas, metricas,
        safeFormatDate
    };
}